<template>
    <v-autocomplete
        ref="autocomplete"
        v-model="selected"
        :label="label"
        :placeholder="placeholder"
        :searchInput.sync="searchCustomers"
        :items="customerRecords"
        :loading="inFlight"
        :rules="rules"
        autoSelectFirst
        clearable
        dense
        hideDetails
        hideNoData
        noFilter
        outlined
        @click:clear="clearButtonHandler()"
    >
        <template v-slot:item="data">
             <v-list-item-content>
                {{ data.item.value.fullName }}
                <br>

                <small v-if="data.item.value.externalRef">
                    {{ data.item.value.externalRef }}
                </small>
                <br v-if="data.item.value.externalRef">

                <small v-if="data.item.value.addressLine1">
                    {{ data.item.value.addressLine1 }}
                </small>
                <br v-if="data.item.value.addressLine1">

                <small v-if="data.item.value.city && data.item.value.state">
                    {{ data.item.value.city }},
                    {{ data.item.value.state }}
                </small>
             </v-list-item-content>
        </template>
        <template v-slot:selection="data">
            {{ data.item.text }}
        </template>
    </v-autocomplete>
</template>

<script>
// Packages
import _ from 'lodash';

// Vuex
import { mapGetters, mapState } from 'vuex';

// Api
import LogisticsApi from '@/api/LogisticsApi';

export default {
    name: 'AutocompleteSuggestCustomers',
    props: {
        value: {
            type: Object,
            required: false,
            default: null,
        },
        label: {
            type: String,
            required: false,
            default: 'Recipient',
        },
        placeholder: {
            type: String,
            required: false,
            default: 'Start typing to Search',
        },
        rules: {
            type: Array,
            required: false,
            default: () => ( [] ),
        },
    },
    data() {
        return {
            /** @type { Boolean } */
            inFlight: false,
            /** @type { Object[] } */
            customerRecords: [],
            /** @type { Null | DebouncedFunc | Function } */
            searchCustomersDebounced: null,
            /** @type { Null | String } */
            searchCustomers: null,
            /** @type { String } */
            customerSearchStr: '',
        };
    },
    computed: {
        ...mapGetters( {
            websiteId: 'Website/id',
        } ),
        ...mapState( {
            jwtToken: ( state ) => state.User.jwtToken,
            userAccountId: ( state ) => state.User.accountId,
        } ),
        /** @returns { Null | Object } */
        selected: {
            get() {
                return this.value;
            },
            set( value ) {
                this.$emit( 'input', value ?? null );
            },
        },
    },
    watch: {
        searchCustomers( input ) {
            this.customerSearchStr = input;
            if ( this.customerSearchStr && this.customerSearchStr !== this.selected ) {
                this.searchCustomersDebounced();
            }
        },
        value() {
            // Clear out old results when we null out the value from the parent
            if ( !this.value ) {
                this.customerRecords = [];
            }
        },
    },
    created() {
        this.searchCustomersDebounced = _.debounce( () => { this.getCustomerSuggestions(); }, 500 );
    },
    mounted() {
        if ( !!this.value && ( this.customerRecords.length === 0 ) ) {
            this.customerRecords = [ {
                text: this.value.fullName,
                value: { ...this.value },
            } ];
        }
    },
    methods: {
        async getCustomerSuggestions() {
            this.inFlight = true;
            try {
                const { data } = await LogisticsApi.getAutoSuggestCustomer( {
                    jwtToken: this.jwtToken,
                    websiteId: this.websiteId,
                    userAccountId: this.userAccountId,
                    searchStr: this.customerSearchStr,
                } );
                /** Format items to ensure v-autocomplete sees ALL data, even when students share a `fullName` */
                this.customerRecords = data.customerSearchDTO
                    ? data.customerSearchDTO.map( ( customer ) => ( {
                        text: customer.fullName,
                        value: customer,
                    } ) )
                    : [];
            } catch ( error ) {
                console.error( 'ERROR getting recipient suggestions: ', error );
            } finally {
                this.inFlight = false;
            }
        },
        clearButtonHandler() {
            this.customerRecords = [];
            this.selected = null;
            this.searchCustomers = null;
            this.customerSearchStr = '';
        },
    },
};
</script>
